import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  DatePicker,
  EmptyState,
  EmptyStateBody,
  Icon,
  Label,
  Pagination,
  Progress,
  ProgressMeasureLocation,
  Spinner,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { AngleLeftIcon, AngleRightIcon, CalendarAltIcon, CalendarDayIcon, ClockIcon, ExclamationTriangleIcon, ExternalLinkAltIcon, PencilAltIcon, TrashIcon } from '@patternfly/react-icons';
import { useKeycloak } from '../KeycloakProvider';
import { Issue, IssuesListResponse, TimeLog, TimeLogsListResponse, UpdateTimeLogRequest } from '../../types';
import { useApiClient, useWorkLogFilterOptions, useWorkLogFiltersUrl, useWorklogEdit } from '../../hooks';
import { useTimeTracker } from '../../context/TimeTrackerContext';
import { useNotifications } from '../../context/NotificationContext';
import { EditTimeLogModal } from '../EditTimeLogModal';
import { ConfirmModal } from '../ConfirmModal';
import { IssueLabelsCell, UserSelect } from '../common';
import { detectOverlaps, formatDateISO, formatTimeOnly, formatTimeSpent, getMonthName, getStatusLabelColor, getWorkingDaysInMonth, isValidJiraIssueKey } from '../../utils';
import { DAILY_GOAL_SECONDS, JIRA_BASE_URL } from '../../config';
import './TimeLogHistory.css';

export const TimeLogHistory: React.FC = () => {
  const { keycloak, initialized } = useKeycloak();
  const apiClient = useApiClient();
  const { currentUser } = useTimeTracker();
  const { notifyError } = useNotifications();
  const notifyErrorRef = useRef(notifyError);
  useEffect(() => { notifyErrorRef.current = notifyError; }, [notifyError]);
  const { availableUsers, isAdmin } = useWorkLogFilterOptions();
  const { initialFilters, updateUrl } = useWorkLogFiltersUrl();

  // Initialize state from URL or defaults
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    if (initialFilters.date) {
      const date = new Date(initialFilters.date);
      if (!isNaN(date.getTime())) return date;
    }
    return new Date();
  });
  const [selectedUserId, setSelectedUserId] = useState<string[]>(() => {
    if (initialFilters.userId) {
      return [initialFilters.userId];
    }
    return [];
  });
  const [timeLogs, setTimeLogs] = useState<TimeLog[]>([]);
  const [summary, setSummary] = useState<{ dailyTotalSeconds: number; monthlyTotalSeconds: number; workingDaysInMonth: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const issuesCacheRef = useRef<Map<string, Issue>>(new Map());
  const [issuesCacheVersion, setIssuesCacheVersion] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Set current user as default when loaded (only if not set from URL)
  useEffect(() => {
    if (currentUser && selectedUserId.length === 0 && !initialFilters.userId) {
      setSelectedUserId([currentUser.id]);
    }
  }, [currentUser, selectedUserId.length, initialFilters.userId]);

  // Update URL when filters change
  useEffect(() => {
    const userId = selectedUserId.length > 0 ? selectedUserId[0] : null;
    updateUrl({
      date: formatDateISO(selectedDate),
      userId,
    });
  }, [selectedDate, selectedUserId, updateUrl]);

  const buildFilter = useCallback((userId: string, startDate: string, endDate: string) => {
    const escapedUserId = userId.replace(/"/g, '\\"');
    return `/workLogs?pageSize=100&filter=authorId="${escapedUserId}" AND startedAt>="${startDate}" AND startedAt<"${endDate}"&orderBy=startedAt desc`;
  }, []);

  const fetchDailyLogs = useCallback(async (date: Date, userId: string) => {
    if (!initialized || !keycloak?.authenticated) return;

    const dayStart = `${formatDateISO(date)}T00:00:00Z`;
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    const dayEnd = `${formatDateISO(nextDay)}T00:00:00Z`;

    setLoading(true);
    try {
      const response = await apiClient.get<TimeLogsListResponse>(buildFilter(userId, dayStart, dayEnd));
      setTimeLogs(response.items);

      // Fetch missing issues
      const missingIssueIds = new Set<string>();
      response.items.forEach((log) => {
        if (!issuesCacheRef.current.has(log.issueId)) {
          missingIssueIds.add(log.issueId);
        }
      });

      if (missingIssueIds.size > 0) {
        const idsArray = Array.from(missingIssueIds);
        const filterParts = idsArray.map((id) => `id="${id}"`).join(' OR ');
        const issuesResponse = await apiClient.get<IssuesListResponse>(
          `/issues?pageSize=100&filter=${encodeURIComponent(filterParts)}`
        );
        issuesResponse.items.forEach((issue) => {
          issuesCacheRef.current.set(issue.id, issue);
        });
        setIssuesCacheVersion((v) => v + 1);
      }
    } catch {
      notifyErrorRef.current('Failed to load logs', 'Could not fetch daily time logs');
    } finally {
      setLoading(false);
    }
  }, [apiClient, initialized, keycloak?.authenticated, buildFilter]);

  const fetchSummary = useCallback(async (date: Date, userId: string) => {
    if (!initialized || !keycloak?.authenticated) return;

    try {
      const response = await apiClient.get<{ dailyTotalSeconds: number; monthlyTotalSeconds: number; workingDaysInMonth: number }>(
        `/workLogs/summary?authorId=${encodeURIComponent(userId)}&date=${formatDateISO(date)}`
      );
      setSummary(response);
    } catch {
      notifyErrorRef.current('Failed to load summary', 'Could not fetch worklog summary');
    }
  }, [apiClient, initialized, keycloak?.authenticated]);

  // Fetch daily logs and summary when date or user changes
  // selectedUserId is set by the effect above when currentUser loads,
  // so we don't need currentUser?.id as a separate dependency
  useEffect(() => {
    const userId = selectedUserId.length > 0 ? selectedUserId[0] : undefined;
    if (userId) {
      fetchDailyLogs(selectedDate, userId);
      fetchSummary(selectedDate, userId);
    }
  }, [selectedDate, selectedUserId, fetchDailyLogs, fetchSummary]);

  const handleDateChange = (_event: React.FormEvent<HTMLInputElement>, value: string) => {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      setSelectedDate(date);
    }
  };

  // Refresh helper for after save/delete
  const refreshAfterChange = useCallback(async () => {
    const userId = selectedUserId.length > 0 ? selectedUserId[0] : undefined;
    if (userId) {
      await Promise.all([
        fetchDailyLogs(selectedDate, userId),
        fetchSummary(selectedDate, userId),
      ]);
    }
  }, [fetchDailyLogs, fetchSummary, selectedDate, selectedUserId]);

  const { editModalProps, deleteModalProps, handleEdit, handleDeleteClick } = useWorklogEdit({
    onSave: useCallback(
      async (id: string, startedTime: string, timeSpentSeconds: number, labels?: string[]) => {
        await apiClient.patch(`/workLogs/${id}`, { startedTime, timeSpentSeconds, ...(labels !== undefined && { labels }) } as UpdateTimeLogRequest);
        // Invalidate issue cache if labels changed so fresh data is fetched
        if (labels !== undefined) {
          const log = timeLogs.find((l) => l.id === id);
          if (log) issuesCacheRef.current.delete(log.issueId);
        }
        await refreshAfterChange();
      },
      [apiClient, timeLogs, refreshAfterChange]
    ),
    onDelete: useCallback(
      async (id: string) => {
        await apiClient.delete(`/workLogs/${id}`);
        await refreshAfterChange();
      },
      [apiClient, refreshAfterChange]
    ),
  });

  const getEndTime = (timeLog: TimeLog): string => {
    const startDate = new Date(timeLog.startedTime);
    const endDate = new Date(startDate.getTime() + timeLog.timeSpentSeconds * 1000);
    return formatTimeOnly(endDate.toISOString());
  };

  // Detect overlapping time logs
  const overlappingLogs = useMemo(() => {
    return detectOverlaps(timeLogs, (log) => {
      return log.issueKey || log.issueId || log.id;
    });
  }, [timeLogs]);

  // Paginate time logs (issuesCacheVersion triggers re-render when issues are loaded)
  const paginatedLogs = useMemo(() => {
    const startIndex = (page - 1) * perPage;
    return timeLogs.slice(startIndex, startIndex + perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLogs, page, perPage, issuesCacheVersion]);

  // Calculate daily stats from summary endpoint
  const dailyStats = useMemo(() => {
    const totalSeconds = summary?.dailyTotalSeconds ?? 0;
    const percentage = Math.min(100, Math.round((totalSeconds / DAILY_GOAL_SECONDS) * 100));
    return { totalSeconds, percentage };
  }, [summary]);

  // Calculate monthly stats from summary endpoint
  const monthlyStats = useMemo(() => {
    const totalSeconds = summary?.monthlyTotalSeconds ?? 0;
    const workingDays = summary?.workingDaysInMonth ?? getWorkingDaysInMonth(selectedDate);
    const monthlyGoalSeconds = workingDays * DAILY_GOAL_SECONDS;
    const percentage = Math.min(100, Math.round((totalSeconds / monthlyGoalSeconds) * 100));
    return { totalSeconds, percentage, monthlyGoalSeconds, workingDays };
  }, [summary, selectedDate]);

  // Get month name for display
  const monthName = useMemo(() => getMonthName(selectedDate), [selectedDate]);

  // Reset page when date changes
  useEffect(() => {
    setPage(1);
  }, [selectedDate]);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            <ClockIcon /> Work log
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="history-toolbar">
            <div className="history-date-nav">
              <Button
                variant="plain"
                icon={<AngleLeftIcon />}
                onClick={() => {
                  const prev = new Date(selectedDate);
                  prev.setDate(prev.getDate() - 1);
                  setSelectedDate(prev);
                }}
                aria-label="Previous day"
                size="sm"
              />
              <DatePicker
                value={formatDateISO(selectedDate)}
                onChange={handleDateChange}
                aria-label="Select date"
              />
              <Button
                variant="plain"
                icon={<AngleRightIcon />}
                onClick={() => {
                  const next = new Date(selectedDate);
                  next.setDate(next.getDate() + 1);
                  setSelectedDate(next);
                }}
                aria-label="Next day"
                size="sm"
              />
            </div>
            {isAdmin && availableUsers.length > 0 && (
              <UserSelect
                availableUsers={availableUsers}
                selectedUsers={selectedUserId}
                onSelectionChange={setSelectedUserId}
                placeholder="Select user..."
                isMultiSelect={false}
              />
            )}
          </div>

          {/* Summary stats section */}
          {!loading && (
            <div className="history-stats-section">
              <div className="history-stat-card">
                <div className="history-stat-label">
                  <CalendarDayIcon /> Selected day
                </div>
                <Progress
                  value={dailyStats.percentage}
                  title={`${formatTimeSpent(dailyStats.totalSeconds)} / 8h`}
                  measureLocation={ProgressMeasureLocation.outside}
                  aria-label="Daily progress"
                />
              </div>
              <div className="history-stat-card">
                <div className="history-stat-label">
                  <CalendarAltIcon /> {monthName}
                </div>
                <Progress
                  value={monthlyStats.percentage}
                  title={`${formatTimeSpent(monthlyStats.totalSeconds)} / ${formatTimeSpent(monthlyStats.monthlyGoalSeconds)} (${monthlyStats.workingDays} days)`}
                  measureLocation={ProgressMeasureLocation.outside}
                  aria-label="Monthly progress"
                />
              </div>
            </div>
          )}

          {loading ? (
            <div className="history-loading">
              <Spinner size="lg" />
            </div>
          ) : timeLogs.length === 0 ? (
            <EmptyState titleText="No time entries yet" icon={ClockIcon} headingLevel="h4">
              <EmptyStateBody>You didn&apos;t log any time on this date.</EmptyStateBody>
            </EmptyState>
          ) : (
            <>
              <Pagination
                itemCount={timeLogs.length}
                perPage={perPage}
                page={page}
                onSetPage={(_event, newPage) => setPage(newPage)}
                onPerPageSelect={(_event, newPerPage) => {
                  setPerPage(newPerPage);
                  setPage(1);
                }}
                perPageOptions={[
                  { title: '10', value: 10 },
                  { title: '20', value: 20 },
                  { title: '50', value: 50 },
                ]}
                variant="top"
                isCompact
                className="history-pagination-top"
              />
              <Table aria-label="Time log history" variant="compact" className="history-table">
                <Thead>
                  <Tr>
                    <Th style={{ width: '1%' }} modifier="nowrap">Key</Th>
                    <Th modifier="truncate">Summary</Th>
                    <Th style={{ width: '1%' }} modifier="nowrap">Labels</Th>
                    <Th style={{ width: '1%' }} modifier="nowrap">Status</Th>
                    <Th style={{ width: '1%' }} modifier="nowrap">Started</Th>
                    <Th style={{ width: '1%' }} modifier="nowrap">Ended</Th>
                    <Th style={{ width: '1%' }} modifier="nowrap">Duration</Th>
                    <Th style={{ width: '1%' }} modifier="nowrap">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedLogs.map((log) => {
                    const overlappingWith = overlappingLogs.get(log.id);
                    const hasOverlap = overlappingWith && overlappingWith.length > 0;
                    const issue = issuesCacheRef.current.get(log.issueId);
                    const issueKey = issue?.key || log.issueKey || log.issueId;
                    const issueSummary = issue?.summary || log.issueSummary || '-';
                    return (
                    <Tr key={log.id} className={hasOverlap ? 'history-row-overlap' : ''}>
                      <Td dataLabel="Key" modifier="nowrap">
                        <span className="history-key-cell">
                          <span className="history-key-label">
                            {hasOverlap && (
                              <Tooltip content={`Overlaps with: ${overlappingWith.join(', ')}`}>
                                <Icon status="warning">
                                  <ExclamationTriangleIcon />
                                </Icon>
                              </Tooltip>
                            )}
                            <strong>{issueKey}</strong>
                          </span>
                          {isValidJiraIssueKey(issueKey) && (
                            <Tooltip content="Open in Jira">
                              <a
                                href={`${JIRA_BASE_URL}/browse/${encodeURIComponent(issueKey)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="history-jira-link"
                                aria-label={`Open ${issueKey} in Jira`}
                              >
                                <ExternalLinkAltIcon />
                              </a>
                            </Tooltip>
                          )}
                        </span>
                      </Td>
                      <Td dataLabel="Summary" modifier="truncate">
                        {issueSummary}
                      </Td>
                      <Td dataLabel="Labels" modifier="nowrap">
                        <IssueLabelsCell labels={issue?.labels} countClassName="history-labels-count" />
                      </Td>
                      <Td dataLabel="Status" modifier="nowrap">
                        {issue?.status ? (
                          <Label color={getStatusLabelColor(issue.status)} isCompact>
                            {issue.status}
                          </Label>
                        ) : (
                          '-'
                        )}
                      </Td>
                      <Td dataLabel="Start" modifier="nowrap">{formatTimeOnly(log.startedTime)}</Td>
                      <Td dataLabel="End" modifier="nowrap">{getEndTime(log)}</Td>
                      <Td dataLabel="Duration" modifier="nowrap">{formatTimeSpent(log.timeSpentSeconds)}</Td>
                      <Td dataLabel="Actions" modifier="nowrap" className="history-col-actions">
                        <Button
                          variant="plain"
                          icon={<PencilAltIcon />}
                          onClick={() => handleEdit({ ...log, issueKey, issueSummary, issueLabels: issue?.labels })}
                          aria-label="Edit"
                          size="sm"
                        />
                        <Button
                          variant="plain"
                          icon={<TrashIcon />}
                          onClick={() => handleDeleteClick(log.id)}
                          aria-label="Delete"
                          size="sm"
                        />
                      </Td>
                    </Tr>
                    );
                  })}
                </Tbody>
              </Table>
              <Pagination
                itemCount={timeLogs.length}
                perPage={perPage}
                page={page}
                onSetPage={(_event, newPage) => setPage(newPage)}
                onPerPageSelect={(_event, newPerPage) => {
                  setPerPage(newPerPage);
                  setPage(1);
                }}
                perPageOptions={[
                  { title: '10', value: 10 },
                  { title: '20', value: 20 },
                  { title: '50', value: 50 },
                ]}
                variant="bottom"
                isCompact
                className="history-pagination-bottom"
              />
            </>
          )}
        </CardBody>
      </Card>

      <EditTimeLogModal {...editModalProps} />
      <ConfirmModal {...deleteModalProps} />
    </>
  );
};
